import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const baseUrl = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const hydrateFromStorage = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        if (isMounted) {
          setIsCheckingAuth(false);
        }
        return;
      }

      const { data, error } = await fetchUserDetails(token);

      if (!isMounted) {
        return;
      }

      if (data) {
        setUser({ token });
        setUserDetails(data);
      } else {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem("token");
          setUser(null);
          setUserDetails(null);
        } else {
          setUser({ token });
        }
      }

      setIsCheckingAuth(false);
    };

    hydrateFromStorage();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get(`${baseUrl}user/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 202) {
        return { data: response.data, error: null };
      }

      return {
        data: null,
        error: new Error(`Unexpected status code: ${response.status}`),
      };
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      return { data: null, error };
    }
  };

  const login = async (identifier, password, type) => {
    try {
      const payload = {
        [type]: identifier,
        password,
      };

      const response = await axios.post(`${baseUrl}auth/login/`, payload);

      if (response.status === 202 && response.data.token) {
        const token = response.data.token;
        localStorage.setItem("token", token);

        const { data } = await fetchUserDetails(token);

        setUser({ token });
        setUserDetails(data ?? null);
        setIsCheckingAuth(false);

        return { success: true, userDetails: data };
      }
    } catch (error) {
      console.error("Login failed:", error);
    }

    return { success: false };
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setUserDetails(null);
    setIsCheckingAuth(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, userDetails, login, logout, isCheckingAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
